import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Check, X, Trash2 } from 'lucide-react';
import BotaoVoltar from '../../../components/BotaoVoltar/BotaoVoltar';
import { useToast } from '../../../components/toast_notification/ToastProvider';
import { detalharPrestador, atualizarPrestador, removerPrestador } from './prestadoresService';
import { PrestadorForm as TForm, FundoVinculo } from './types';
import { PrestadorCampos } from './PrestadorForm';
import { maskCnpj } from '../fundo_liquido/fields';
import { PAPEL } from '../fundo_liquido/mock';
import '../fundo_detalhe/style.css';
import '../fundo_liquido/style.css';

const papelLabel = (v: string): string => PAPEL.find((o) => o.value === v)?.label ?? v;
const val = (x?: string): string => (x && String(x).trim() ? String(x) : '—');

const SkBar: React.FC<{ w?: string | number; h?: number }> = ({ w = '100%', h = 12 }) => (
  <span className="fd-sk" style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }} />
);
const SkeletonDetalhe: React.FC<{ onVoltar: () => void }> = ({ onVoltar }) => (
  <div className="fd-screen">
    <div className="fd-head">
      <div className="fd-head-row">
        <div className="fd-head-main">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BotaoVoltar onClick={onVoltar} />
            <SkBar w="260px" h={24} />
          </div>
          <div style={{ marginTop: 8 }}><SkBar w="150px" h={13} /></div>
        </div>
        <div className="fd-actions"><SkBar w={96} h={34} /></div>
      </div>
    </div>
    <div className="fd-body fl-resumo" aria-busy="true" aria-label="Carregando prestador">
      <div className="fl-rev-section">
        <div className="fd-sk-sectitle"><SkBar w="110px" h={10} /></div>
        <div className="fl-rev-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="fl-rev-item"><SkBar w="50%" h={9} /><div style={{ height: 4 }} /><SkBar w="80%" h={13} /></div>
          ))}
        </div>
      </div>
      <div className="fl-rev-section">
        <div className="fd-sk-sectitle"><SkBar w="140px" h={10} /></div>
        <SkBar h={30} /><div style={{ height: 8 }} /><SkBar h={30} />
      </div>
    </div>
  </div>
);

interface Props {
  prestadorId: number;
  onVoltar: () => void;
  onExcluido?: () => void;
}

const Item: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="fl-rev-item"><span className="k">{k}</span><span className="val">{v}</span></div>
);

const PrestadorDetalhe: React.FC<Props> = ({ prestadorId, onVoltar, onExcluido }) => {
  const toast = useToast();
  const [salvo, setSalvo] = useState<TForm | null>(null);
  const [form, setForm] = useState<TForm | null>(null);
  const [fundos, setFundos] = useState<FundoVinculo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const d = await detalharPrestador(prestadorId);
      setSalvo(d.form);
      setForm(d.form);
      setFundos(d.fundos);
      setEditando(false);
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar o prestador.');
      setSalvo(null);
      setForm(null);
    } finally {
      setCarregando(false);
    }
  }, [prestadorId]);

  useEffect(() => { carregar(); }, [carregar]);

  const patch = (p: Partial<TForm>) => setForm((prev) => (prev ? { ...prev, ...p } : prev));
  const entrar = () => { setForm(salvo); setEditando(true); };
  const cancelar = () => { setForm(salvo); setEditando(false); };

  const salvar = async () => {
    if (salvando || !form) return;
    if (!form.nome.trim()) { toast.error('Informe o nome do prestador.'); return; }
    setSalvando(true);
    try {
      await atualizarPrestador(prestadorId, form);
      toast.success('Prestador atualizado com sucesso.');
      await carregar();
    } catch (e: any) {
      const det = e?.details ? ` — ${JSON.stringify(e.details)}` : '';
      toast.error(`Falha ao salvar: ${e?.message ?? 'erro'}${det}`);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (excluindo) return;
    if (!window.confirm('Excluir este prestador? Esta ação faz exclusão lógica.')) return;
    setExcluindo(true);
    try {
      await removerPrestador(prestadorId);
      toast.success('Prestador excluído.');
      (onExcluido ?? onVoltar)();
    } catch (e: any) {
      toast.error(e?.message ?? 'Falha ao excluir.');
    } finally {
      setExcluindo(false);
    }
  };

  if (carregando || (!salvo && !erro)) return <SkeletonDetalhe onVoltar={onVoltar} />;
  if (erro || !salvo) {
    return (
      <div className="fd-screen">
        <div className="fd-head">
          <BotaoVoltar onClick={onVoltar} />
        </div>
        <div className="fd-body"><div className="fl-empty">{erro ?? 'Prestador não encontrado.'}</div></div>
      </div>
    );
  }

  return (
    <div className="fd-screen">
      <div className="fd-head">
        <div className="fd-head-row">
          <div className="fd-head-main">
            <div className="fd-title-row">
              <BotaoVoltar onClick={onVoltar} />
              <h1 className="fd-title">{salvo.nome}</h1>
              {editando && <span className="fd-editing-tag">Editando</span>}
            </div>
            <div className="fd-meta"><span>{salvo.cnpj ? maskCnpj(salvo.cnpj.replace(/\D/g, '')) : 'sem CNPJ'}</span></div>
          </div>
          <div className="fd-actions">
            {editando ? (
              <>
                <button className="fd-btn fd-btn--ghost" onClick={cancelar} type="button" disabled={salvando}><X size={15} /> Cancelar</button>
                <button className="fd-btn fd-btn--primary" onClick={salvar} type="button" disabled={salvando}><Check size={15} /> {salvando ? 'Salvando...' : 'Salvar'}</button>
              </>
            ) : (
              <>
                <button className="fd-btn fd-btn--ghost" onClick={excluir} type="button" disabled={excluindo}><Trash2 size={15} /> {excluindo ? 'Excluindo...' : 'Excluir'}</button>
                <button className="fd-btn fd-btn--outline" onClick={entrar} type="button"><Pencil size={15} /> Editar</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="fd-body fl-resumo">
        {editando && form ? (
          <div className="fl-rev-section">
            <div className="fl-section-title">Dados do prestador</div>
            <PrestadorCampos form={form} onChange={patch} />
          </div>
        ) : (
          <div className="fl-rev-section">
            <div className="fl-section-title">Dados do prestador</div>
            <div className="fl-rev-grid">
              <Item k="CNPJ" v={salvo.cnpj ? maskCnpj(salvo.cnpj.replace(/\D/g, '')) : '—'} />
              <Item k="Nome" v={val(salvo.nome)} />
              <Item k="Grupo econômico" v={val(salvo.grupo_economico_nome || salvo.grupo_economico)} />
              <Item k="Papéis" v={salvo.papeis.length ? salvo.papeis.map(papelLabel).join(', ') : '—'} />
              <Item k="É ANGA" v={salvo.is_anga ? 'Sim' : 'Não'} />
            </div>
          </div>
        )}

        <div className="fl-rev-section">
          <div className="fl-section-title">Fundos vinculados ({fundos.length})</div>
          {fundos.length === 0 ? (
            <div className="fl-empty">Este prestador não está vinculado a nenhum fundo.</div>
          ) : (
            <table className="fl-table">
              <thead><tr><th>Fundo</th><th>CNPJ</th><th>Papel</th><th>Início</th><th>Fim</th></tr></thead>
              <tbody>
                {fundos.map((f) => (
                  <tr key={f.id}>
                    <td>{val(f.nome_fantasia || f.razao_social)}</td>
                    <td>{f.fundo_cnpj ? maskCnpj(f.fundo_cnpj) : '—'}</td>
                    <td>{papelLabel(f.papel)}</td>
                    <td>{val(f.data_inicio)}</td>
                    <td>{val(f.data_fim)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrestadorDetalhe;
